import React, { useEffect, useState } from "react";
import { toastr } from "react-redux-toastr";
import { useForm } from "react-hook-form";
import {
  Company,
  Phone,
  EmailAddress, Sector,
  Industry,
  CompanySegment,
  City
} from "../../../core/api/models";
import States from "../../../utils/states.json";
import "react-widgets/dist/css/react-widgets.css";
import api from "../../../api";
import { useDataLoader } from "react-remote-data-hooks";
import { decodeHtmlEntities } from "../../../utils/common";
import { CreateCompanyGeneralForm } from "./create-company-general-form";
import { CreateCompanySocialMediaForm } from "./create-company-social-media-form";
import { CreateCompanyGlobalForm } from "./create-company-global-form";

const CreateCompany: React.FunctionComponent<{
  error?: string;
  onCreateCompany: (data: Company) => void;
  setStage?: (stage: string) => void;
  phones?: Phone[];
  emails?: EmailAddress[];
  civilId?: string;
  searchQuery?: string;
  gridColumns?: 1 | 2 | 3;
}> = (props) => {
  const [isEmailEnable, setIsEmailEnable] = useState<boolean>(true);
  const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
  const [selectedPhone, setSelectedPhone] = useState<Phone[] | undefined>(props.phones);
  const [selectedEmail, setSelectedEmail] = useState<EmailAddress[] | undefined>(props.emails);
  const [selectedValues, setSelectedValues] = useState<{ tenantsName: string; tenantsID: number; }[]>([]);
  const [districtList, setDistrictList] = useState([]);
  const [cities, setCities] = useState<{ name: string; value: string }[]>([]);

  const { control, handleSubmit, errors, clearErrors, watch, setValue } = useForm<Company>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const { data: sectors } = useDataLoader<Sector[]>(() => api.sector.loadAllSector());

  const { data: industries } = useDataLoader<Industry[]>(() => api.industry.loadAllIndustry());

  const { data: companySegments } = useDataLoader<CompanySegment[]>(() =>
    api.companySegment.loadAllCompanySegment()
  );

  const statesList = watch("country") === "India" || !watch("country") ? States : [];

  useEffect(() => {
    setDistrictList([]);
    setValue("stateName", undefined);
    setValue("district", undefined);
    setValue("cityId", undefined);
  }, [watch("country")])

  React.useEffect(() => {
    if (props.searchQuery) {
      setValue("name", props.searchQuery);
    }

    const fetchCityData = async () => {
      try {
        const cityData = await api.AddNewRequirement.loadAllCities();
        const cityOptions = cityData?.map((item: City) => ({
          value: item._id,
          name: decodeHtmlEntities(item?.cityName),
        }));
        cityOptions.sort((a, b) => a.name.localeCompare(b.name));
        setCities(cityOptions);
      } catch (error) {
        // console.log("Failed to fetch city data:", error);
      }
    };
    fetchCityData();
  }, []);

  const getFormatedFormData = (fData: any) => {
    const addresses = [{
      // _id: props.selectedAddress?._id,
      country: fData?.country,
      state: fData?.stateName,
      district: fData?.district,
      // city: fData?.city?.name,
      cityObjectId: fData?.cityId?.value || fData?.cityId,
      address: fData?.address,
      // postalCode: fData?.zipCode,
      isPrimary: true,
    }];
    return {
      name: fData?.name?.trim(),
      websiteUrl: fData?.websiteUrl,
      industry: fData?.industryId,
      cinNumber: fData?.cinNumber?.trim(),
      companyPan: fData?.companyPan,
      gstinNumber: fData?.gstinNumber?.trim(),
      originCountry: fData?.originCountry,
      incorporatedDate: fData.incorporatedDate,
      sector: fData?.sectorId,
      companySegment: fData?.companySegmentId,
      revenue: fData?.revenue || '',
      authorisedCapital: fData?.authorisedCapital,
      paidCapitalAmount: fData?.paidCapitalAmount,
      companyCertifications: fData?.companyCertifications || undefined,
      membership: fData?.membership || undefined,
      employeesInIndia: fData?.employeesInIndia,
      totalEmployees: fData?.totalEmployees,
      regionalOffice: fData?.regionalOffice || undefined,
      groupCompanyDetails: fData?.groupCompanyDetails?.map((item: any) => item?.value || item) || undefined,
      businessNumber: fData?.businessNumber,
      fax: fData?.fax,
      tollFree: fData.tollFree,
      businessMail: fData?.businessMail,
      salesMail: fData?.salesMail,
      suportMail: fData?.suportMail,
      headQuatersEmail: fData?.headQuatersEmail,
      linkedin: fData?.linkedin,
      facebook: fData?.facebook,
      googlePlus: fData?.googlePlus,
      twitter: fData?.twitter,
      mailRestriction: fData?.mailRestriction?.value || fData?.mailRestriction,
      country: fData?.country,
      zipCode: fData?.zipCode,
      stateName: fData?.stateName,
      address: fData?.address,
      phones: fData?.phones || undefined,
      addresses: addresses || [],
      district: fData?.district,
      class: fData?.class,
      companyType: fData?.companyType,
      activity: fData?.activity,
      activityDescription: fData?.activityDescription,
      roc: fData?.roc,
      groupCompanyTenantDetails: selectedValues || [],
      emails: selectedEmail,
    };
  };

  const onSubmit = async (fData: any) => {
    setIsSubmiting(true)
    const updateData = getFormatedFormData(fData)
    try {
      const response = await api.company.createCompany(updateData);
      props.onCreateCompany(response);
      setIsSubmiting(false)
      toastr.success("Success", "Company saved successfully");
    } catch (error) {
      setIsSubmiting(false)
      toastr.error("Error", error?.response?.data?.message);
    }
  }

  const onChangePhone = (phoneNumber: Phone[]) => {
    setSelectedPhone(phoneNumber);
  };

  const onChangeEmail = (emailAdd: EmailAddress[]) => {
    setSelectedEmail(emailAdd);
  };

  const validateProtocol = (value?: string) => {
    if (value && value.trim() !== "") {
      const protocolRegex = /^(http|https):\/\//;
      const urlRegex = /^(https?:\/\/)?([\w\d.-]+\.[a-z]{2,})(:[0-9]+)?(\/.*)?$/i;
      const isValidProtocol = protocolRegex.test(value);
      const isValidUrl = urlRegex.test(value);

      if (!isValidProtocol) {
        return "Please enter a URL with http:// or https://";
      } else if (!isValidUrl) {
        return "Please enter a valid URL";
      } else {
        return true;
      }
    }
    return true; // Return true to indicate no validation error when value is empty or undefined
  };

  const classOptions = [
    { value: 'One Person Company', name: 'One Person Company' },
    { value: 'Private', name: 'Private' },
    { value: 'Public', name: 'Public' },
  ];

  const companyTypeOptions = [
    { name: 'State government company', value: 'State government company' },
    { name: 'Union government company', value: 'Union government company' },
    { name: 'Guarantee and association Company', value: 'Guarantee and association Company' },
    { name: 'Non-government company', value: 'Non-government company' },
    { name: 'Subsidiary of company incorporated outside India', value: 'Subsidiary of company incorporated outside India' },
  ];

  const ROCOptions = [
    { name: 'AHMEDABAD', value: 'AHMEDABAD' },
    { name: 'ANDAMAN', value: 'ANDAMAN' },
    { name: 'BANGALORE', value: 'BANGALORE' },
    { name: 'CHANDIGARH', value: 'CHANDIGARH' },
    { name: 'CHENNAI', value: 'CHENNAI' },
    { name: 'CHHATTISGARH', value: 'CHHATTISGARH' },
    { name: 'COIMBATORE', value: 'COIMBATORE' },
    { name: 'CUTTACK', value: 'CUTTACK' },
    { name: 'DELHI', value: 'DELHI' },
    { name: 'ERNAKULAM', value: 'ERNAKULAM' },
    { name: 'GOA', value: 'GOA' },
    { name: 'GWALIOR', value: 'GWALIOR' },
    { name: 'HIMACHAL PRADESH', value: 'HIMACHAL PRADESH' },
    { name: 'HYDERABAD', value: 'HYDERABAD' },
    { name: 'JAIPUR', value: 'JAIPUR' },
    { name: 'JAMMU', value: 'JAMMU' },
    { name: 'JHARKHAND', value: 'JHARKHAND' },
    { name: 'KANPUR', value: 'KANPUR' },
    { name: 'KOLKATA', value: 'KOLKATA' },
    { name: 'MUMBAI', value: 'MUMBAI' },
    { name: 'PATNA', value: 'PATNA' },
    { name: 'PONDICHERRY', value: 'PONDICHERRY' },
    { name: 'PUNE', value: 'PUNE' },
    { name: 'SHILLONG', value: 'SHILLONG' },
    { name: 'UTTARAKHAND', value: 'UTTARAKHAND' },
    { name: 'VIJAYAWADA', value: 'VIJAYAWADA' },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="col-12 row mt-4">

        <CreateCompanyGeneralForm
          classOptions={classOptions}
          companySegments={companySegments}
          companyTypeOptions={companyTypeOptions}
          control={control}
          errors={errors}
          industries={industries}
          ROCOptions={ROCOptions}
          sectors={sectors}
          selectedValues={selectedValues}
          setSelectedValues={setSelectedValues}
          validateProtocol={validateProtocol}
        />

        <CreateCompanySocialMediaForm
          control={control}
          errors={errors}
          validateProtocol={validateProtocol}
        />

        <CreateCompanyGlobalForm
          cities={cities}
          control={control}
          districtList={districtList}
          errors={errors}
          isEmailEnable={isEmailEnable}
          onChangeEmail={onChangeEmail}
          onChangePhone={onChangePhone}
          props={props}
          selectedEmail={selectedEmail}
          selectedPhone={selectedPhone}
          setDistrictList={setDistrictList}
          setValue={setValue}
          statesList={statesList}
          watch={watch}
        />
      </div>
      <div className="row ml-0 align-items-center">
        <div className="form-group pt-2 col-md-4 col-12 ">
          <button
            type="submit"
            disabled={isSubmiting}
            className="btn btn-primary btn-block font-normal font-weight-bold"
          >
            Create Company
          </button>
        </div>
        {props.setStage && (
          <button
            type="button"
            onClick={() => props.setStage?.("fetch-customer")}
            className="btn btn-outline-dark btn-block font-normal font-weight-bold btn-goback text-gray-500 mb-2 px-0 col-md-4 col-12"
          >
            Go Back
          </button>
        )}
      </div>
    </form>
  );
};

export default CreateCompany;
